import type { RefObject } from 'react';
import { useRef, useState, useCallback } from 'react';

interface UseAudioRecorderReturn {
  isRecording: boolean;
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
  cancel: () => void;
  error: string | null;
  analyserRef: RefObject<AnalyserNode | null>;
}

const MAX_RECORDING_MS = 120_000; // 2 minutes

/** Preferred MIME types in order — pick the first one the browser supports. */
const MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
];

function getSupportedMimeType(): string {
  for (const mime of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return ''; // browser default
}

export function useAudioRecorder(): UseAudioRecorderReturn {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resolverRef = useRef<((blob: Blob) => void) | null>(null);
  const mimeRef = useRef<string>('');

  // Mic analyser for audio-reactive visuals
  const analyserRef = useRef<AnalyserNode | null>(null);
  const micContextRef = useRef<AudioContext | null>(null);

  const start = useCallback(async () => {
    setError(null);
    chunksRef.current = [];

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError('Microphone access denied');
      throw new Error('Microphone access denied');
    }

    // Set up analyser for mic level visualization
    const micCtx = new AudioContext();
    micContextRef.current = micCtx;
    const source = micCtx.createMediaStreamSource(stream);
    const analyser = micCtx.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.8;
    source.connect(analyser);
    // Connect through silent gain so the analyser processes audio
    const silentGain = micCtx.createGain();
    silentGain.gain.value = 0;
    analyser.connect(silentGain);
    silentGain.connect(micCtx.destination);
    analyserRef.current = analyser;

    const mimeType = getSupportedMimeType();
    mimeRef.current = mimeType;

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      // Stop all tracks to release the microphone
      stream.getTracks().forEach((t) => t.stop());

      // Tear down mic analyser
      if (micContextRef.current) {
        micContextRef.current.close().catch(() => {});
        micContextRef.current = null;
      }
      analyserRef.current = null;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }

      // Return raw WebM/Opus blob — Whisper accepts it natively, no re-encoding needed
      const rawBlob = new Blob(chunksRef.current, {
        type: mimeRef.current || 'audio/webm',
      });

      if (resolverRef.current) {
        resolverRef.current(rawBlob);
        resolverRef.current = null;
      }
    };

    recorder.start();
    setIsRecording(true);

    // Safety timeout — auto-stop after max duration
    timerRef.current = setTimeout(() => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      }
    }, MAX_RECORDING_MS);
  }, []);

  const stop = useCallback((): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state !== 'recording') {
        reject(new Error('Not recording'));
        return;
      }

      resolverRef.current = resolve;
      recorder.stop();
      setIsRecording(false);
    });
  }, []);

  /** Stop the recorder and discard the audio — doesn't resolve the stop promise. */
  const cancel = useCallback(() => {
    // Clear the resolver so onstop doesn't deliver the blob
    resolverRef.current = null;

    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state === 'recording') {
      recorder.stop();
    }
    setIsRecording(false);
  }, []);

  return { isRecording, start, stop, cancel, error, analyserRef };
}
